export default function Faq() {
  return (
    <div className="min-h-screen bg-background">
      <div className="h-screen max-h-screen overflow-y-auto">
        <div className="px-5 pt-4 pb-9 max-w-[50rem] mx-auto flex flex-col gap-6">
          <h1 className="text-3xl font-black text-text tracking-tight">
            FAQ / How Step Syncing Works
          </h1>
          <p className="text-text-muted text-base leading-6">
            Hey there! Welcome to FootSlapping. If you are wondering how your steps get from your
            phone or watch to the competition leaderboard, you are in the right place. Here is
            everything you need to know, explained in plain English.
          </p>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">
              How syncing works (the short version)
            </h2>
            <p className="text-text-muted text-sm leading-6">
              FootSlapping reads your step data from your device's health platform. On iOS and Apple
              Watch, that is Apple Health. On Android, it is Health Connect, which can pull in data
              from Fitbit, Samsung Health, Wear OS, and other sources. Step syncing only happens in
              the iOS and Android apps — there is no way to sync steps from a browser.
            </p>
            <p className="text-text-muted text-sm leading-6">
              Once your steps are read, they are sent securely to our server and stored per day.
              That daily step count is what powers the leaderboards in your competitions. The app
              only reads step counts — nothing else from your health data.
            </p>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">Apple Health / Apple Watch (iOS)</h2>
            <p className="text-text-muted text-sm leading-6">
              On iOS, everything happens right on your device. No OAuth, no cloud login — just a
              simple permission tap.
            </p>
            <ul className="text-text-muted text-sm leading-6 list-disc pl-5 flex flex-col gap-2">
              <li>
                When you first connect, the app asks for HealthKit permission. FootSlapping only
                reads your step count — no heart rate, no sleep data, no workouts.
              </li>
              <li>
                Steps are read directly from Apple Health using HealthKit, Apple's built-in health
                data framework. Your step data never leaves your device until the app sends it to
                our server.
              </li>
              <li>
                Background sync runs automatically via iOS background fetch. iOS manages this on its
                own schedule, with a minimum interval of about 10 hours. You cannot force a
                background sync to happen right away — that is up to iOS.
              </li>
              <li>
                You can always trigger a manual sync from the app. Just head to your dashboard and
                tap the Apple Health card or pull to refresh.
              </li>
              <li>
                Silent push notifications can also trigger a sync. During the server-side sync cycle
                (every 12 hours for users in active competitions), our server sends a silent push to
                your iOS device via Expo Push. The app wakes briefly in the background, syncs your
                Apple Health data, and goes back to sleep — all without a banner or sound.
              </li>
              <li>
                To connect, just tap the Apple Health card in your settings or dashboard. Grant
                permission, and you are all set.
              </li>
            </ul>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">
              Health Connect (Android / Fitbit / Samsung Health)
            </h2>
            <p className="text-text-muted text-sm leading-6">
              On Android, step syncing uses Health Connect, Android's on-device health data store.
              This gives you the flexibility to pull steps from multiple sources, right on your
              phone — no Google account sign-in or OAuth consent screen involved.
            </p>
            <ul className="text-text-muted text-sm leading-6 list-disc pl-5 flex flex-col gap-2">
              <li>
                When you first connect, the app asks for Health Connect permission. FootSlapping
                only reads your step count.
              </li>
              <li>
                Steps are read directly from Health Connect on your device. It aggregates step data
                from your Android device sensors, Wear OS watch, Fitbit, Samsung Health, and other
                apps that write into Health Connect.
              </li>
              <li>
                Sync happens when you open the app (on-demand) and also via silent push, the same as
                iOS — our server sends a silent push roughly every 12 hours for users in active
                competitions, and the app syncs quietly in the background.
              </li>
              <li>
                To connect, just tap the Health Connect card in your settings or dashboard. Grant
                permission, and you are all set.
              </li>
            </ul>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">Used to sync with Google Health?</h2>
            <p className="text-text-muted text-sm leading-6">
              We have discontinued Google Health OAuth step sync. If your account is still connected
              to Google Health, open the app — you will see a prompt to connect Apple Health or
              Health Connect instead, and to disconnect Google Health once you have. On the web,
              head to your Health Connections settings for the same prompt and a link to download
              the app. Your past synced step history is unaffected; only new syncing changes.
            </p>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">Sync frequency at a glance</h2>
            <ul className="text-text-muted text-sm leading-6 list-disc pl-5 flex flex-col gap-2">
              <li>
                iOS: background fetch roughly every 10 hours (iOS-controlled), silent push when the
                server needs fresh data, plus manual trigger anytime.
              </li>
              <li>
                Android: on-demand when you open the app, plus server-side cron every 12 hours for
                active competition members.
              </li>
              <li>
                Sync is throttled to avoid too many API calls in a short period. You will not see
                steps update every second — that is intentional, to be respectful of the health
                platform APIs.
              </li>
              <li>
                The app only syncs date ranges that matter: the dates of your active competitions,
                or the last several days if you trigger a manual sync.
              </li>
            </ul>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">Troubleshooting</h2>
            <p className="text-text-muted text-sm leading-6">
              Steps not showing up? Do not panic. Here are the most common fixes:
            </p>
            <h3 className="text-lg font-bold text-text">On iOS</h3>
            <ul className="text-text-muted text-sm leading-6 list-disc pl-5 flex flex-col gap-2">
              <li>
                Check that Health permissions are still granted. Go to Settings &gt; Health &gt;
                Data Access &amp; Devices &gt; FootSlapping and make sure Steps is toggled on.
              </li>
              <li>
                Make sure Background App Refresh is enabled. Go to Settings &gt; General &gt;
                Background App Refresh and confirm it is on for FootSlapping.
              </li>
              <li>
                Allow push notifications. Silent push sync requires notification permissions. Go to
                Settings &gt; Notifications &gt; FootSlapping and make sure Allow Notifications is
                on. The app never sends visible alerts — only silent background syncs.
              </li>
            </ul>
            <h3 className="text-lg font-bold text-text">On Android</h3>
            <ul className="text-text-muted text-sm leading-6 list-disc pl-5 flex flex-col gap-2">
              <li>
                Check that Health Connect permissions are still granted. Open Health Connect (or the
                Health Connect card in FootSlapping settings) and confirm Steps access is on for
                FootSlapping.
              </li>
              <li>
                If sync is failing, try reconnecting Health Connect. Go to settings, disconnect,
                then reconnect. This refreshes the on-device permission grant.
              </li>
            </ul>
            <h3 className="text-lg font-bold text-text">General tips</h3>
            <ul className="text-text-muted text-sm leading-6 list-disc pl-5 flex flex-col gap-2">
              <li>
                Wait for the next sync cycle before worrying. Pull to refresh or tap the sync button
                to trigger a manual sync.
              </li>
              <li>
                Double-check the competition date range. Steps outside the competition start and end
                dates will not appear on the leaderboard, even if they synced successfully.
              </li>
              <li>
                Make sure you actually walked with your device. If your phone or watch was sitting
                on a desk, there are no steps to sync.
              </li>
            </ul>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">Step data limits</h2>
            <p className="text-text-muted text-sm leading-6">
              To keep things fair and our database healthy, we enforce a few sensible limits:
            </p>
            <ul className="text-text-muted text-sm leading-6 list-disc pl-5 flex flex-col gap-2">
              <li>
                Maximum 150,000 steps per day. If you somehow record more than that, the extra steps
                are capped. (For context, 150,000 steps is roughly 70-75 miles of walking — about
                three marathons.)
              </li>
              <li>No negative step values. You cannot undo steps you already took.</li>
              <li>No future dates. Step counts are recorded against real calendar days only.</li>
            </ul>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">Still have questions?</h2>
            <p className="text-text-muted text-sm leading-6">
              We are happy to help. Reach out to us at support@footslapping.com and we will get back
              to you. Happy stepping!
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
